import {
  Button,
  FormControl,
  Radio,
  ReactSelect,
  Label,
  Form,
} from "@hydra/atom/components";
import { components } from "react-select";
import PropTypes from "prop-types";
import { useMutation } from "@tanstack/react-query";
import { kebabCase, startCase } from "lodash";

import {
  Header,
  FormLeftHeader,
  CustomizedDatePicker,
  Select,
  CustomSwitch,
} from "@/components/common";
import { PrependInput } from "@/components/admin";
import {
  setFormValue,
  setNextStep,
} from "@/reducers/admin/companySettingsReducer.js";
import { nestedOptionStyles } from "@/utils/select/constants";
import { getFormattedLedgerAccounts } from "@/api/finance/ledgerAccountApi";
import { formatGLAccountOption } from "@/utils/finance/helpers";
import { updateCompanySettingFinancial } from "@/api/admin/companySettingApi";
import {
  createCompanyAccount,
  updateCompanyAccount,
} from "@/api/admin/companyAccountApi";
import { prepareFinancialData, getAccountsData } from "@/utils/admin/helpers";
import showToast from "@/utils/toast/helpers";
import { getTestId } from "@/utils/helpers";
import { currencies, validationMessages } from "@/utils/admin/constants";

function Option(props) {
  const { data, testId } = props;
  return (
    <div data-testid={getTestId(`${testId}-Option`)}>
      <components.Option {...props}>
        <div className="react-select-option">
          <p>{data.label}</p>
          <p>{data.subtitle}</p>
        </div>
      </components.Option>
    </div>
  );
}

Option.propTypes = {
  data: PropTypes.object.isRequired,
};

function FinancialInformation({ state, dispatch }) {
  const leftContent = (title, subtitle) => (
    <div className="container-header-left-content">
      <div className="title">{title}</div>
      <div className="subtitle">{subtitle}</div>
    </div>
  );

  const updateMutation = useMutation(
    ({ id: recordId, data: recordData }) =>
      updateCompanySettingFinancial(recordId, recordData),
    {
      onError: () => {
        showToast("Could not update. Try again!", "error");
      },
      onSuccess: () => {
        showToast("Updated successfully", "success");
        dispatch(setNextStep(3));
      },
    }
  );

  const updateLinkedCompanyAccount = async (companyAccountId, dataObject) => {
    try {
      if (companyAccountId) {
        await updateCompanyAccount(companyAccountId, dataObject);
      } else {
        await createCompanyAccount(dataObject);
      }
    } catch (error) {
      showToast(
        `Could not setup company account for ${dataObject.accountType}. Try again!`,
        "error"
      );
    }
  };

  const handleSubmit = () => {
    const formattedData = prepareFinancialData(state);
    const accounts = getAccountsData(state);
    accounts.forEach((account) => {
      updateLinkedCompanyAccount(account.id, account);
    });

    updateMutation.mutate({
      id: state.companyId,
      data: {
        ...formattedData,
        $Type: "finance",
      },
    });
  };

  return (
    <section className="financial-setting-body">
      <Form onSubmit={handleSubmit} shouldScrollOnError>
        <div className="setting-header">
          <Header
            leftContent={leftContent(
              "Finance Information",
              "Get emails to find out what’s going on when you’re not online. You can turn them off anytime."
            )}
          />
          <hr className="full-hr" />
        </div>
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Tax Registration Number"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <PrependInput
                  value={state.taxRegistrationNumber}
                  onChange={(value) =>
                    dispatch(setFormValue("taxRegistrationNumber", value))}
                  name="Tax Registration Number"
                  id="taxRegistrationNumber"
                  prependText="TXNUM"
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="System Currency"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <ReactSelect
                  name="currency"
                  placeholder="Select currency"
                  options={currencies}
                  value={state.systemCurrency}
                  onChange={(value) =>
                    dispatch(setFormValue("systemCurrency", value))}
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Local Currency"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <ReactSelect
                  name="currency"
                  placeholder="Select currency"
                  options={currencies}
                  value={state?.localCurrency}
                  onChange={(value) =>
                    dispatch(setFormValue("localCurrency", value))}
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Do you have Standard Chart of Accounts System"
              subtitle="Select to specify if segments of information should be added to the standard account codes."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-4">
                <div
                  className={
                    state.chartAccountSystem ?
                      "form-radio-card-primary" :
                      "card"
                  }
                  role="presentation"
                  onClick={() =>
                    dispatch(setFormValue("chartAccountSystem", true))}
                >
                  <Radio
                    className="radio-text"
                    label="Yes"
                    itemValue
                    type="radio"
                    value={state.chartAccountSystem}
                  >
                    <Radio.CheckIcon>
                      <span className="material-icons">circle</span>
                    </Radio.CheckIcon>
                  </Radio>
                  <h1 className="form-field-radio-text">
                    We use a standard system
                  </h1>
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className={
                    !state.chartAccountSystem ?
                      "form-radio-card-primary" :
                      "card"
                  }
                  role="presentation"
                  onClick={() =>
                    dispatch(setFormValue("chartAccountSystem", false))}
                >
                  <Radio
                    className="radio-text"
                    label="No"
                    itemValue
                    type="radio"
                    value={!state.chartAccountSystem}
                    onChange={() => {}}
                  >
                    <Radio.CheckIcon>
                      <span className="material-icons">circle</span>
                    </Radio.CheckIcon>
                  </Radio>
                  <h1 className="form-field-radio-text">
                    We don’t use standard system
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Fiscal Calendar Year"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-4">
                <CustomizedDatePicker
                  name="startFiscalCalendarYear"
                  value={state.startFiscalCalendarYear}
                  onChange={(value) =>
                    dispatch(
                      setFormValue("startFiscalCalendarYear", value || null)
                    )}
                />
              </div>
              <div className="col-md-4">
                <CustomizedDatePicker
                  name="endFiscalCalendarYear"
                  value={state.endFiscalCalendarYear}
                  onChange={(value) =>
                    dispatch(setFormValue("endFiscalCalendarYear", value))}
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Customer Activity Alert Options"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-4">
                <CustomSwitch
                  value={state.creditLimit}
                  onChange={() =>
                    dispatch(setFormValue("creditLimit", !state.creditLimit))}
                  title="Credit Limit"
                  subtitle="Save my login details for next time."
                />
                <CustomSwitch
                  value={state.commitmentLimit}
                  onChange={() =>
                    dispatch(
                      setFormValue("commitmentLimit", !state.commitmentLimit)
                    )}
                  title="Commitment Limit"
                  subtitle="Save my login details for next time."
                />
                <CustomSwitch
                  value={state.reservation}
                  onChange={() =>
                    dispatch(setFormValue("reservation", !state.reservation))}
                  title="Reservation"
                  subtitle="Save my login details for next time."
                />
                <CustomSwitch
                  value={state.arInvoice}
                  onChange={() =>
                    dispatch(setFormValue("arInvoice", !state.arInvoice))}
                  title="AR Invoice"
                  subtitle="Save my login details for next time."
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Default Company Account Settings:"
              subtitle=""
            />
          </div>
          <div className="col-md-9">
            <div className="row gy-4">
              {state.accountTypes.map((accountType) => (
                <div className="col-md-6">
                  <FormControl>
                    <Label
                      htmlFor={kebabCase(accountType)}
                      label={`${startCase(accountType)} A/C`}
                    />
                    <Select
                      id={accountType}
                      name={accountType}
                      queryKey="ledger-account"
                      placeholder="Select account"
                      value={state[accountType]}
                      onChange={(value) =>
                        dispatch(setFormValue(accountType, value))}
                      optionsLoader={() => getFormattedLedgerAccounts()}
                      formatOption={formatGLAccountOption}
                      dataAccessKey="data"
                      optionLabelKey="name"
                      optionValueKey="id"
                      hasNestedOptions
                      styles={nestedOptionStyles}
                      components={{ Option }}
                      isClearable
                      rules="required"
                      messages={validationMessages.required}
                      isDisabled
                    />
                  </FormControl>
                </div>
              ))}
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <footer className="setting-footer">
          <Button
            small
            className="save-btn"
            type="submit"
            loading={updateMutation.isLoading}
          >
            Save and Move Next
          </Button>
        </footer>
      </Form>
    </section>
  );
}

export default FinancialInformation;
